/**
 * PFPGoalTouchesCard
 *
 * Shows the therapist WhatsApp `LOG` observations that parents recently
 * reported against their child's PFP priority goals. Powered by
 * `GET /api/children/{child_id}/pfp-goal-touches` (Phase 3 endpoint).
 *
 * Rendered inside the "Goals & progress" collapsible in ChildProfile —
 * only when the therapist has starred at least one goal AND at least one
 * touch exists. Silent when empty so the UI doesn't nag.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function _formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function PFPGoalTouchesCard({ childId, priorityGoals }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openGoals, setOpenGoals] = useState({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(
          `${API}/children/${childId}/pfp-goal-touches`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [childId, priorityGoals?.length]);

  if (loading || !data) return null;
  if ((data.total_touches || 0) === 0) return null;

  const grouped = data.touches_by_goal || {};

  return (
    <div
      className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-emerald-50/60 to-white border border-emerald-200/70"
      data-testid="pfp-goal-touches-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-emerald-600" />
        <span className="text-sm font-semibold text-emerald-900">
          Parent-reported moments · {data.total_touches}
        </span>
        <span className="text-xs text-stone-500">via WhatsApp LOG</span>
      </div>
      <ul className="space-y-2">
        {(data.priority_goals || []).map((goal) => {
          const touches = grouped[goal] || [];
          if (touches.length === 0) return null;
          const isOpen = openGoals[goal] ?? touches.length <= 2;
          const preview = isOpen ? touches : touches.slice(0, 2);
          return (
            <li key={goal} className="rounded-xl bg-white/70 border border-emerald-100 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-emerald-900 flex-1">
                  ⭐ {goal}
                </span>
                <span className="text-xs text-stone-500 tabular-nums">
                  {touches.length} moment{touches.length !== 1 ? 's' : ''}
                </span>
                {touches.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setOpenGoals({ ...openGoals, [goal]: !isOpen })}
                    className="text-emerald-700 hover:text-emerald-900 p-1"
                    data-testid={`pfp-touches-toggle-${goal.slice(0, 12)}`}
                    aria-label={isOpen ? 'Show fewer' : 'Show all'}
                  >
                    {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
              <ul className="mt-2 space-y-1.5">
                {preview.map((t) => (
                  <li key={t.id} className="text-xs text-stone-700 leading-relaxed">
                    <span className="text-stone-400 mr-1.5 tabular-nums">
                      {_formatWhen(t.created_at)}
                    </span>
                    &ldquo;{(t.notes || '').slice(0, 180)}
                    {t.notes && t.notes.length > 180 ? '…' : ''}&rdquo;
                    {t.new_words && t.new_words.length > 0 && (
                      <span className="ml-1.5 text-emerald-700 font-medium">
                        [{t.new_words.slice(0, 3).join(', ')}]
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
